import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { auth } from '../lib/auth';
import '../assets/css/AdminDashboard.css';

// Enhanced Job Modal Component with formatting hints
const JobModal = ({ job, formData, onChange, onSubmit, onClose, mode }) => {
  const categories = ['virtual-assistant', 'customer-service', 'marketing', 'technology'];
  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Freelance'];
  const jobLevels = ['Entry Level', 'Mid Level', 'Senior Level', 'Executive'];

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h3>{mode === 'create' ? 'Create New Job Opening' : 'Edit Job Opening'}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Job Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={onChange}
                required
                placeholder="e.g., Virtual Assistant - E-commerce"
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select name="category" value={formData.category} onChange={onChange} required>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Job Type *</label>
              <select name="type" value={formData.type} onChange={onChange} required>
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Experience Level *</label>
              <select name="level" value={formData.level} onChange={onChange} required>
                {jobLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={onChange}
                required
                placeholder="e.g., Remote, Manila, etc."
              />
            </div>

            <div className="form-group full-width">
              <label>Job Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={onChange}
                required
                rows="3"
                placeholder="Describe the job role and responsibilities. Each sentence will become a bullet point. Example: Handle customer inquiries via email and chat. Manage product listings and updates."
              />
              <small className="format-hint">
                💡 Tip: End each key point with a period to create separate bullet points.
              </small>
            </div>

            <div className="form-group full-width">
              <label>Requirements</label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={onChange}
                rows="3"
                placeholder="List the requirements. Example: 2+ years of customer service experience. Proficiency with CRM software. Excellent communication skills."
              />
              <small className="format-hint">
                💡 Tip: End each requirement with a period to create separate bullet points.
              </small>
            </div>

            <div className="form-group full-width">
              <label>Benefits</label>
              <textarea
                name="benefits"
                value={formData.benefits}
                onChange={onChange}
                rows="2"
                placeholder="List the benefits offered. Example: Work from home flexibility. Competitive salary. Health insurance."
              />
              <small className="format-hint">
                💡 Tip: End each benefit with a period to create separate bullet points.
              </small>
            </div>

            <div className="form-group full-width">
              <label>Key Responsibilities</label>
              <textarea
                name="responsibilities"
                value={formData.responsibilities}
                onChange={onChange}
                rows="2"
                placeholder="Describe key responsibilities. Example: Respond to customer inquiries within 24 hours. Process orders and returns. Maintain customer satisfaction metrics."
              />
              <small className="format-hint">
                💡 Tip: End each responsibility with a period to create separate bullet points.
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={onChange}
                />
                Active Job Opening
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'create' ? 'Create Job' : 'Update Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// The rest of your original AdminDashboard component remains exactly the same...
const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [applications, setApplications] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canView: true
  });
  
  // CRUD States for Job Openings only
  const [editingJob, setEditingJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'create', 'edit'
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all'
  });

  // Application search and filter states
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [applicationFilters, setApplicationFilters] = useState({
    status: 'all',
    position: 'all'
  });

  // Consultation search and filter states
  const [consultationSearchTerm, setConsultationSearchTerm] = useState('');
  const [consultationFilters, setConsultationFilters] = useState({
    status: 'all',
    type: 'all'
  });

  // Application Status Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: 'new',
    notes: ''
  });

  // Consultation Status Modal
  const [showConsultationStatusModal, setShowConsultationStatusModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [consultationStatusForm, setConsultationStatusForm] = useState({
    status: 'new',
    notes: ''
  });

  // Job Form State
  const [jobForm, setJobForm] = useState({
    title: '',
    category: 'virtual-assistant',
    type: 'Full-time',
    level: 'Entry Level',
    location: 'Remote',
    description: '',
    requirements: '',
    benefits: '',
    responsibilities: '',
    active: true
  });

  useEffect(() => {
    const admin = auth.getCurrentAdmin();
    setCurrentAdmin(admin);
    
    // Set permissions based on admin role
    if (admin) {
      const permissions = {
        canCreate: admin.role === 'super_admin' || admin.role === 'admin',
        canEdit: admin.role === 'super_admin' || admin.role === 'admin',
        canDelete: admin.role === 'super_admin',
        canView: true
      };
      setUserPermissions(permissions);
    }
    
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch applications (Read-only)
      const { data: appsData, error: appsError } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!appsError) setApplications(appsData || []);
      
      // Fetch consultations (Read-only)
      const { data: consultsData, error: consultsError } = await supabase
        .from('consultations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!consultsError) setConsultations(consultsData || []);
      
      // Fetch job openings (CRUD enabled)
      const { data: jobsData, error: jobsError } = await supabase
        .from('job_openings')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!jobsError) setJobOpenings(jobsData || []);
      
      // Calculate stats
      const statusCounts = appsData?.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {}) || {};

      setStats({
        totalApplications: appsData?.length || 0,
        totalConsultations: consultsData?.length || 0,
        totalJobs: jobsData?.length || 0,
        activeJobs: jobsData?.filter(job => job.active).length || 0,
        todayApplications: appsData?.filter(app => 
          new Date(app.created_at).toDateString() === new Date().toDateString()
        ).length || 0,
        todayConsultations: consultsData?.filter(consult =>
          new Date(consult.created_at).toDateString() === new Date().toDateString()
        ).length || 0,
        statusCounts
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Application Status Management
  const updateApplicationStatus = async (applicationId, statusData) => {
    try {
      const { data: updatedApp, error } = await supabase
        .from('applications')
        .update({
          status: statusData.status,
          status_notes: statusData.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId)
        .select()
        .single();

      if (error) throw error;

      setApplications(prev => prev.map(app => 
        app.id === applicationId ? updatedApp : app
      ));
      
      setShowStatusModal(false);
      setSelectedApplication(null);
      setStatusForm({ status: 'new', notes: '' });
      
      return updatedApp;
    } catch (error) {
      console.error('Error updating application status:', error);
      throw error;
    }
  };

  const openStatusModal = (application) => {
    setSelectedApplication(application);
    setStatusForm({
      status: application.status || 'new',
      notes: application.status_notes || ''
    });
    setShowStatusModal(true);
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateApplicationStatus(selectedApplication.id, statusForm);
      alert('Application status updated successfully!');
    } catch (error) {
      alert('Error updating status: ' + error.message);
    }
  };

  // Consultation Status Management
  const updateConsultationStatus = async (consultationId, statusData) => {
    try {
      const { data: updatedConsultation, error } = await supabase
        .from('consultations')
        .update({
          status: statusData.status,
          status_notes: statusData.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', consultationId)
        .select()
        .single();

      if (error) throw error;

      setConsultations(prev => prev.map(consult => 
        consult.id === consultationId ? updatedConsultation : consult
      ));
      
      setShowConsultationStatusModal(false);
      setSelectedConsultation(null);
      setConsultationStatusForm({ status: 'new', notes: '' });
      
      return updatedConsultation;
    } catch (error) {
      console.error('Error updating consultation status:', error);
      throw error;
    }
  };

  const openConsultationStatusModal = (consultation) => {
    setSelectedConsultation(consultation);
    setConsultationStatusForm({
      status: consultation.status || 'new',
      notes: consultation.status_notes || ''
    });
    setShowConsultationStatusModal(true);
  };

  const handleConsultationStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateConsultationStatus(selectedConsultation.id, consultationStatusForm);
      alert('Consultation status updated successfully!');
    } catch (error) {
      alert('Error updating consultation status: ' + error.message);
    }
  };

  // CRUD Operations for Job Openings Only
  const createJob = async (jobData) => {
    try {
      const { data: newJob, error } = await supabase
        .from('job_openings')
        .insert([jobData])
        .select()
        .single();

      if (error) throw error;

      setJobOpenings(prev => [newJob, ...prev]);
      setShowJobModal(false);
      resetJobForm();
      return newJob;
    } catch (error) {
      console.error('Error creating job:', error);
      throw error;
    }
  };

  const updateJob = async (id, jobData) => {
    try {
      const { data: updatedJob, error } = await supabase
        .from('job_openings')
        .update(jobData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setJobOpenings(prev => prev.map(job => job.id === id ? updatedJob : job));
      setShowJobModal(false);
      setEditingJob(null);
      resetJobForm();
      return updatedJob;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job opening?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('job_openings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setJobOpenings(prev => prev.filter(job => job.id !== id));
      setStats(prev => ({ ...prev, totalJobs: prev.totalJobs - 1 }));

    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Error deleting job opening. Please try again.');
    }
  };

  const toggleJobStatus = async (id, currentStatus) => {
    try {
      const { data: updatedJob, error } = await supabase
        .from('job_openings')
        .update({ active: !currentStatus })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setJobOpenings(prev => prev.map(job => job.id === id ? updatedJob : job));

    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Error updating job status. Please try again.');
    }
  };

  // Read-only operations for applications and consultations
  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setApplications(prev => prev.filter(app => app.id !== id));
      setStats(prev => ({ ...prev, totalApplications: prev.totalApplications - 1 }));

    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Error deleting application. Please try again.');
    }
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consultation?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('consultations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setConsultations(prev => prev.filter(consult => consult.id !== id));
      setStats(prev => ({ ...prev, totalConsultations: prev.totalConsultations - 1 }));

    } catch (error) {
      console.error('Error deleting consultation:', error);
      alert('Error deleting consultation. Please try again.');
    }
  };

  // Job Form Handlers
  const resetJobForm = () => {
    setJobForm({
      title: '',
      category: 'virtual-assistant',
      type: 'Full-time',
      level: 'Entry Level',
      location: 'Remote',
      description: '',
      requirements: '',
      benefits: '',
      responsibilities: '',
      active: true
    });
  };

  const handleJobFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'create') {
        await createJob(jobForm);
        alert('Job opening created successfully!');
      } else {
        await updateJob(editingJob.id, jobForm);
        alert('Job opening updated successfully!');
      }
    } catch (error) {
      alert('Error saving job opening: ' + error.message);
    }
  };

  // Modal Handlers for Jobs
  const openCreateJobModal = () => {
    setModalType('create');
    setEditingJob(null);
    resetJobForm();
    setShowJobModal(true);
  };

  const openEditJobModal = (job) => {
    setModalType('edit');
    setEditingJob(job);
    setJobForm({
      title: job.title,
      category: job.category,
      type: job.type,
      level: job.level,
      location: job.location,
      description: job.description,
      requirements: job.requirements || '',
      benefits: job.benefits || '',
      responsibilities: job.responsibilities || '',
      active: job.active
    });
    setShowJobModal(true);
  };

  // Filter functions
  const filterJobs = () => {
    let filtered = jobOpenings;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(job => job.category === filters.category);
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(job => 
        filters.status === 'active' ? job.active : !job.active
      );
    }

    return filtered;
  };

  const filterApplications = () => {
    let filtered = applications;

    // Search filter
    if (applicationSearchTerm) {
      filtered = filtered.filter(app => 
        app.first_name.toLowerCase().includes(applicationSearchTerm.toLowerCase()) ||
        app.last_name.toLowerCase().includes(applicationSearchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(applicationSearchTerm.toLowerCase()) ||
        app.position.toLowerCase().includes(applicationSearchTerm.toLowerCase())
      );
    }

    // Status filter
    if (applicationFilters.status !== 'all') {
      filtered = filtered.filter(app => app.status === applicationFilters.status);
    }

    // Position filter
    if (applicationFilters.position !== 'all') {
      filtered = filtered.filter(app => app.position === applicationFilters.position);
    }

    return filtered;
  };

  const filterConsultations = () => {
    let filtered = consultations;

    // Search filter
    if (consultationSearchTerm) {
      filtered = filtered.filter(consult => 
        consult.name.toLowerCase().includes(consultationSearchTerm.toLowerCase()) ||
        consult.email.toLowerCase().includes(consultationSearchTerm.toLowerCase()) ||
        consult.company?.toLowerCase().includes(consultationSearchTerm.toLowerCase()) ||
        consult.message.toLowerCase().includes(consultationSearchTerm.toLowerCase())
      );
    }

    // Status filter
    if (consultationFilters.status !== 'all') {
      filtered = filtered.filter(consult => consult.status === consultationFilters.status);
    }

    // Type filter
    if (consultationFilters.type !== 'all') {
      filtered = filtered.filter(consult => consult.type === consultationFilters.type);
    }

    return filtered;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function for status badges
const getStatusBadge = (status) => {
  const statusConfig = {
    'new': { label: 'New', class: 'status-new', emoji: '🆕' },
    'for interview': { label: 'For Interview', class: 'status-interview', emoji: '📅' },
    'hired': { label: 'Hired', class: 'status-hired', emoji: '✅' },
    'pending': { label: 'Pending', class: 'status-pending', emoji: '⏳' },
    'declined': { label: 'Declined', class: 'status-declined', emoji: '❌' },
    'csv_downloaded': { label: 'CSV Downloaded', class: 'status-csv', emoji: '📥' } // Add this line
  };
  
  const config = statusConfig[status] || { label: status, class: 'status-default', emoji: '❓' };
  return (
    <span className={`status-badge ${config.class}`}>
      {config.emoji} {config.label}
    </span>
  );
};

  const refreshData = () => {
    fetchAllData();
  };

  // CSV Download Functions
  const convertToCSV = (data, headers) => {
    if (!data || data.length === 0) return '';
    
    const csvHeaders = headers.map(h => h.label).join(',');
    const csvRows = data.map(row => 
      headers.map(header => {
        let value = row[header.key] || '';
        // Handle special formatting
        if (header.format === 'date' && value) {
          value = new Date(value).toLocaleDateString();
        }
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadApplicationsCSV = () => {
    const headers = [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'position', label: 'Position' },
      { key: 'work_availability', label: 'Work Availability' },
      { key: 'software_tools', label: 'Software Tools' },
      { key: 'skills', label: 'Skills' },
      { key: 'cover_letter', label: 'Google Drive Link' },
      { key: 'status', label: 'Status' },
      { key: 'status_notes', label: 'Status Notes' },
      { key: 'created_at', label: 'Applied Date', format: 'date' }
    ];
    
    const csvContent = convertToCSV(applications, headers);
    const filename = `applications_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const downloadConsultationsCSV = () => {
    const headers = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'company', label: 'Company' },
      { key: 'type', label: 'Type' },
      { key: 'message', label: 'Message' },
      { key: 'status', label: 'Status' },
      { key: 'status_notes', label: 'Status Notes' },
      { key: 'preferred_date', label: 'Preferred Date', format: 'date' },
      { key: 'preferred_time', label: 'Preferred Time' },
      { key: 'timezone', label: 'Timezone' },
      { key: 'created_at', label: 'Date', format: 'date' }
    ];
    
    const csvContent = convertToCSV(consultations, headers);
    const filename = `consultations_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const downloadJobsCSV = () => {
    const headers = [
      { key: 'title', label: 'Job Title' },
      { key: 'category', label: 'Category' },
      { key: 'type', label: 'Type' },
      { key: 'level', label: 'Level' },
      { key: 'location', label: 'Location' },
      { key: 'description', label: 'Description' },
      { key: 'requirements', label: 'Requirements' },
      { key: 'benefits', label: 'Benefits' },
      { key: 'responsibilities', label: 'Responsibilities' },
      { key: 'active', label: 'Active' },
      { key: 'created_at', label: 'Created Date', format: 'date' }
    ];
    
    const csvContent = convertToCSV(jobOpenings, headers);
    const filename = `job_openings_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>BPD Admin Dashboard</h1>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  const filteredJobs = filterJobs();

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>BPD Online Corp - Admin Dashboard</h1>
          {currentAdmin && (
            <p className="admin-welcome">
              Welcome, {currentAdmin.full_name} | 
              <span className={`role-badge ${currentAdmin.role}`}>
                {currentAdmin.role === 'super_admin' ? 'Super Admin' : 
                 currentAdmin.role === 'admin' ? 'Admin' : 
                 currentAdmin.role === 'support' ? 'Support' : currentAdmin.role}
              </span>
            </p>
          )}
        </div>
        <div className="header-actions">
          <button onClick={refreshData} className="refresh-btn">Refresh</button>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Applications</h3>
          <p className="stat-number">{stats.totalApplications}</p>
          <span className="stat-today">Today: {stats.todayApplications}</span>
        </div>
        <div className="stat-card">
          <h3>Total Consultations</h3>
          <p className="stat-number">{stats.totalConsultations}</p>
          <span className="stat-today">Today: {stats.todayConsultations}</span>
        </div>
        <div className="stat-card">
          <h3>Job Openings</h3>
          <p className="stat-number">{stats.totalJobs}</p>
          <span className="stat-today">Active: {stats.activeJobs}</span>
        </div>
        <div className="stat-card">
          <h3>Application Status</h3>
          <div className="status-mini-stats">
            <span>New: {stats.statusCounts?.new || 0}</span>
            <span>Interview: {stats.statusCounts?.['for interview'] || 0}</span>
            <span>Hired: {stats.statusCounts?.hired || 0}</span>
          </div>
        </div>
      </div>

      {/* Search and Filters - Show for Jobs, Applications, and Consultations tabs */}
      {(activeTab === 'jobs' || activeTab === 'applications' || activeTab === 'consultations') && (
        <div className="controls-bar">
          <div className="search-box">
            <input
              type="text"
              placeholder={
                activeTab === 'jobs' ? "Search job titles or descriptions..." : 
                activeTab === 'applications' ? "Search applicants by name, email, or position..." :
                "Search consultations by name, email, company, or message..."
              }
              value={
                activeTab === 'jobs' ? searchTerm : 
                activeTab === 'applications' ? applicationSearchTerm :
                consultationSearchTerm
              }
              onChange={(e) => {
                if (activeTab === 'jobs') setSearchTerm(e.target.value);
                else if (activeTab === 'applications') setApplicationSearchTerm(e.target.value);
                else setConsultationSearchTerm(e.target.value);
              }}
              className="search-input"
            />
          </div>
          <div className="filters">
            {activeTab === 'jobs' ? (
              <>
                <select 
                  value={filters.category} 
                  onChange={(e) => setFilters(prev => ({...prev, category: e.target.value}))}
                >
                  <option value="all">All Categories</option>
                  <option value="virtual-assistant">Virtual Assistant</option>
                  <option value="customer-service">Customer Service</option>
                  <option value="marketing">Marketing</option>
                  <option value="technology">Technology</option>
                </select>
                <select 
                  value={filters.status} 
                  onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </>
            ) : activeTab === 'applications' ? (
              <>
                <select 
                  value={applicationFilters.status} 
                  onChange={(e) => setApplicationFilters(prev => ({...prev, status: e.target.value}))}
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="for interview">For Interview</option>
                  <option value="hired">Hired</option>
                  <option value="pending">Pending</option>
                  <option value="declined">Declined</option>
                  <option value="csv_downloaded">CSV Downloaded</option> {/* Add this line */}
                </select>
                <select 
                  value={applicationFilters.position} 
                  onChange={(e) => setApplicationFilters(prev => ({...prev, position: e.target.value}))}
                >
                  <option value="all">All Positions</option>
                  {[...new Set(applications.map(app => app.position))].map(position => (
                    <option key={position} value={position}>{position}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <select 
                  value={consultationFilters.status} 
                  onChange={(e) => setConsultationFilters(prev => ({...prev, status: e.target.value}))}
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select 
                  value={consultationFilters.type} 
                  onChange={(e) => setConsultationFilters(prev => ({...prev, type: e.target.value}))}
                >
                  <option value="all">All Types</option>
                  <option value="consultation">Consultation</option>
                  <option value="contact">Contact</option>
                </select>
              </>
            )}
          </div>
          {activeTab === 'jobs' && userPermissions.canCreate && (
            <button 
              onClick={openCreateJobModal}
              className="create-btn"
            >
              + Add New Job
            </button>
          )}
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={activeTab === 'applications' ? 'active' : ''}
          onClick={() => setActiveTab('applications')}
        >
          Applications ({applications.length})
        </button>
        <button 
          className={activeTab === 'consultations' ? 'active' : ''}
          onClick={() => setActiveTab('consultations')}
        >
          Consultations ({consultations.length})
        </button>
        <button 
          className={activeTab === 'jobs' ? 'active' : ''}
          onClick={() => setActiveTab('jobs')}
        >
          Job Openings ({jobOpenings.length})
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <h2>Recent Activity</h2>
            <div className="recent-activity">
              <div className="recent-section">
                <h3>Latest Applications (5 most recent)</h3>
                {applications.slice(0, 5).map(app => (
                  <div key={app.id} className="activity-item">
                    <span className="activity-type">📄 Application</span>
                    <span className="activity-details">
                      {app.first_name} {app.last_name} applied for {app.position}
                    </span>
                    <span className="activity-status">
                      {getStatusBadge(app.status)}
                    </span>
                    <span className="activity-time">{formatDate(app.created_at)}</span>
                  </div>
                ))}
              </div>
              <div className="recent-section">
                <h3>Latest Job Openings</h3>
                {jobOpenings.slice(0, 5).map(job => (
                  <div key={job.id} className="activity-item">
                    <span className="activity-type">💼 Job Opening</span>
                    <span className="activity-details">
                      {job.title} - {job.type}
                    </span>
                    <span className="activity-time">
                      {job.active ? '🟢 Active' : '🔴 Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'applications' && (
          <ApplicationsTableView 
            data={filterApplications()}
            onUpdateStatus={openStatusModal}
            onDelete={deleteApplication}
            onDownloadCSV={downloadApplicationsCSV}
            permissions={userPermissions}
          />
        )}

        {activeTab === 'consultations' && (
          <ConsultationsTableView 
            data={filterConsultations()}
            onUpdateStatus={openConsultationStatusModal}
            onDelete={deleteConsultation}
            onDownloadCSV={downloadConsultationsCSV}
            permissions={userPermissions}
          />
        )}

        {activeTab === 'jobs' && (
          <JobsTableView 
            data={filteredJobs}
            onEdit={openEditJobModal}
            onDelete={deleteJob}
            onToggleStatus={toggleJobStatus}
            onDownloadCSV={downloadJobsCSV}
            permissions={userPermissions}
          />
        )}
      </div>

      {/* Job Modal */}
      {showJobModal && (
        <JobModal 
          job={editingJob}
          formData={jobForm}
          onChange={handleJobFormChange}
          onSubmit={handleJobSubmit}
          onClose={() => setShowJobModal(false)}
          mode={modalType}
        />
      )}

      {/* Application Status Modal */}
      {showStatusModal && (
        <StatusModal 
          application={selectedApplication}
          formData={statusForm}
          onChange={(e) => setStatusForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
          }))}
          onSubmit={handleStatusSubmit}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      {/* Consultation Status Modal */}
      {showConsultationStatusModal && (
        <ConsultationStatusModal 
          consultation={selectedConsultation}
          formData={consultationStatusForm}
          onChange={(e) => setConsultationStatusForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
          }))}
          onSubmit={handleConsultationStatusSubmit}
          onClose={() => setShowConsultationStatusModal(false)}
        />
      )}
    </div>
  );
};

// Applications Table View with Status Management
const ApplicationsTableView = ({ data, onUpdateStatus, onDelete, onDownloadCSV, permissions }) => {
  if (data.length === 0) {
    return <p className="no-data">No applications found.</p>;
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h3>Job Applications Management</h3>
        <div className="table-header-actions">
          <span className="record-count">{data.length} applications</span>
          <button 
            onClick={onDownloadCSV}
            className="download-csv-btn"
            title="Download as CSV"
          >
            📥 Download CSV
          </button>
        </div>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Applicant</th>
            <th>Position</th>
            <th>Contact</th>
            <th>Work Availability</th>
            <th>Software Tools</th>
            <th>Skills</th>
            <th>Google Drive Link</th>
            <th>Status</th>
            <th>Applied Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(application => (
            <tr key={application.id}>
              <td>
                <strong>{application.first_name} {application.last_name}</strong>
              </td>
              <td>{application.position}</td>
              <td>
                <div>{application.email}</div>
                <div className="phone">{application.phone || 'No phone'}</div>
              </td>
              <td>
                <span className="availability-badge">
                  {application.work_availability || 'Not specified'}
                </span>
              </td>
              <td>
                <div className="tools-skills-list">
                  {application.software_tools ? (
                    application.software_tools.split(', ').map((tool, index) => (
                      <span key={index} className="tool-skill-tag">
                        {tool}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">None specified</span>
                  )}
                </div>
              </td>
              <td>
                <div className="tools-skills-list">
                  {application.skills ? (
                    application.skills.split(', ').map((skill, index) => (
                      <span key={index} className="tool-skill-tag">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">None specified</span>
                  )}
                </div>
              </td>
              <td>
                {application.cover_letter ? (
                  <a 
                    href={application.cover_letter} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="drive-link"
                    title="Open Google Drive link"
                  >
                    📎 View Resume
                  </a>
                ) : (
                  <span className="text-muted">No link provided</span>
                )}
              </td>
              <td>
                {getStatusBadge(application.status)}
                {application.status_notes && (
                  <div className="status-notes-preview">
                    {application.status_notes.substring(0, 50)}...
                  </div>
                )}
              </td>
              <td>{new Date(application.created_at).toLocaleDateString()}</td>
              <td className="actions-cell">
                {permissions.canEdit && (
                  <button 
                    onClick={() => onUpdateStatus(application)}
                    className="status-btn"
                    title="Update Status"
                  >
                    📊
                  </button>
                )}
                {permissions.canDelete && (
                  <button 
                    onClick={() => onDelete(application.id)}
                    className="delete-btn"
                    title="Delete application"
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Status Modal Component
// Status Modal Component
const StatusModal = ({ application, formData, onChange, onSubmit, onClose }) => {
  const statusOptions = [
    { value: 'new', label: '🆕 New' },
    { value: 'for interview', label: '📅 For Interview' },
    { value: 'hired', label: '✅ Hired' },
    { value: 'pending', label: '⏳ Pending' },
    { value: 'declined', label: '❌ Declined' },
    { value: 'csv_downloaded', label: '📥 CSV Downloaded' } // Add this line
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Update Application Status</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-group">
            <label>Applicant</label>
            <p className="applicant-info">
              {application?.first_name} {application?.last_name} - {application?.position}
            </p>
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select 
              name="status" 
              value={formData.status} 
              onChange={onChange} 
              required
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={onChange}
              rows="4"
              placeholder="Add notes about this status change (interview schedule, reasons, etc.)..."
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Consultation Status Modal Component
const ConsultationStatusModal = ({ consultation, formData, onChange, onSubmit, onClose }) => {
  const statusOptions = [
    { value: 'new', label: '🆕 New' },
    { value: 'scheduled', label: '📅 Scheduled' },
    { value: 'completed', label: '✅ Completed' },
    { value: 'cancelled', label: '❌ Cancelled' }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Update Consultation Status</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-group">
            <label>Consultation Request</label>
            <p className="applicant-info">
              {consultation?.name} - {consultation?.company || 'No company'}
            </p>
            <p className="applicant-info">
              {consultation?.email} {consultation?.phone && `| ${consultation.phone}`}
            </p>
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select 
              name="status" 
              value={formData.status} 
              onChange={onChange} 
              required
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={onChange}
              rows="4"
              placeholder="Add notes about this consultation (schedule details, outcome, etc.)..."
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function for status badges
const getStatusBadge = (status) => {
  const statusConfig = {
    'new': { label: 'New', class: 'status-new', emoji: '🆕' },
    'for interview': { label: 'For Interview', class: 'status-interview', emoji: '📅' },
    'hired': { label: 'Hired', class: 'status-hired', emoji: '✅' },
    'pending': { label: 'Pending', class: 'status-pending', emoji: '⏳' },
    'declined': { label: 'Declined', class: 'status-declined', emoji: '❌' }
  };
  
  const config = statusConfig[status] || { label: status, class: 'status-default', emoji: '❓' };
  return (
    <span className={`status-badge ${config.class}`}>
      {config.emoji} {config.label}
    </span>
  );
};

// Consultations Table View with Status Management
const ConsultationsTableView = ({ data, onUpdateStatus, onDelete, onDownloadCSV, permissions }) => {
  if (data.length === 0) {
    return <p className="no-data">No consultations found.</p>;
  }

  const getConsultationStatusBadge = (status) => {
    const statusConfig = {
      'new': { label: 'New', class: 'status-new', emoji: '🆕' },
      'scheduled': { label: 'Scheduled', class: 'status-interview', emoji: '📅' },
      'completed': { label: 'Completed', class: 'status-hired', emoji: '✅' },
      'cancelled': { label: 'Cancelled', class: 'status-declined', emoji: '❌' }
    };
    
    const config = statusConfig[status] || { label: status, class: 'status-default', emoji: '❓' };
    return (
      <span className={`status-badge ${config.class}`}>
        {config.emoji} {config.label}
      </span>
    );
  };

  return (
    <div className="table-container">
      <div className="table-header">
        <h3>Consultation Requests Management</h3>
        <div className="table-header-actions">
          <span className="record-count">{data.length} consultations</span>
          <button 
            onClick={onDownloadCSV}
            className="download-csv-btn"
            title="Download as CSV"
          >
            📥 Download CSV
          </button>
        </div>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            <th>Company</th>
            <th>Preferred Time</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(consultation => (
            <tr key={consultation.id}>
              <td>
                <strong>{consultation.name}</strong>
                {consultation.message && (
                  <div className="consultation-preview">
                    {consultation.message.substring(0, 80)}...
                  </div>
                )}
              </td>
              <td>
                <div>{consultation.email}</div>
                <div className="phone">{consultation.phone || 'No phone'}</div>
              </td>
              <td>{consultation.company || '-'}</td>
              <td>
                {consultation.preferred_date && consultation.preferred_time ? (
                  <div>
                    <div>{new Date(consultation.preferred_date).toLocaleDateString()}</div>
                    <div className="phone">{consultation.preferred_time} {consultation.timezone}</div>
                  </div>
                ) : (
                  <span className="text-muted">Not specified</span>
                )}
              </td>
              <td>
                {getConsultationStatusBadge(consultation.status)}
                {consultation.status_notes && (
                  <div className="status-notes-preview">
                    {consultation.status_notes.substring(0, 50)}...
                  </div>
                )}
              </td>
              <td>{new Date(consultation.created_at).toLocaleDateString()}</td>
              <td className="actions-cell">
                {permissions.canEdit && (
                  <button 
                    onClick={() => onUpdateStatus(consultation)}
                    className="status-btn"
                    title="Update Status"
                  >
                    📊
                  </button>
                )}
                {permissions.canDelete && (
                  <button 
                    onClick={() => onDelete(consultation.id)}
                    className="delete-btn"
                    title="Delete consultation"
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Jobs Table View with Full CRUD
const JobsTableView = ({ data, onEdit, onDelete, onToggleStatus, onDownloadCSV, permissions }) => {
  if (data.length === 0) {
    return <p className="no-data">No job openings found.</p>;
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h3>Job Openings Management</h3>
        <div className="table-header-actions">
          <span className="record-count">{data.length} jobs</span>
          <button 
            onClick={onDownloadCSV}
            className="download-csv-btn"
            title="Download as CSV"
          >
            📥 Download CSV
          </button>
        </div>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(job => (
            <tr key={job.id}>
              <td>
                <strong>{job.title}</strong>
                <div className="job-description-preview">
                  {job.description.substring(0, 60)}...
                </div>
              </td>
              <td>{job.category}</td>
              <td>{job.type}</td>
              <td>
                <span className={`status-badge ${job.active ? 'active' : 'inactive'}`}>
                  {job.active ? '🟢 Active' : '🔴 Inactive'}
                </span>
              </td>
              <td>{new Date(job.created_at).toLocaleDateString()}</td>
              <td className="actions-cell">
                {permissions.canEdit && (
                  <button 
                    onClick={() => onEdit(job)}
                    className="edit-btn"
                    title="Edit job"
                  >
                    ✏️
                  </button>
                )}
                {permissions.canEdit && (
                  <button 
                    onClick={() => onToggleStatus(job.id, job.active)}
                    className={`status-btn ${job.active ? 'deactivate' : 'activate'}`}
                    title={job.active ? 'Deactivate' : 'Activate'}
                  >
                    {job.active ? '⏸️' : '▶️'}
                  </button>
                )}
                {permissions.canDelete && (
                  <button 
                    onClick={() => onDelete(job.id)}
                    className="delete-btn"
                    title="Delete job"
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminDashboard;